import type { Dispatch, FC, ReactNode } from 'react';
import { createContext, useContext, useMemo, useReducer } from 'react';

import { Action, bankTransferRequestNewReducer, State } from './reducer';

type Context = State & {
  dispatch: Dispatch<Action>;
};

interface BankTransferRequestsNewProviderProps {
  children: ReactNode;
}

const BankTransferRequestsNewContext = createContext<Context | undefined>(
  undefined,
);

export const useBankTransferRequestsNewState = (): Context => {
  const context = useContext(BankTransferRequestsNewContext);

  if (!context) {
    throw new Error(
      'useBankTransferRequestsNewState must be used within a <BankTransferRequestsNewProvider/>',
    );
  }

  return context;
};

const BankTransferRequestsNewProvider: FC<
  BankTransferRequestsNewProviderProps
> = ({ children }) => {
  const [state, dispatch] = useReducer(bankTransferRequestNewReducer, {
    recipientInformation: {
      country: 'FR',
      iban: '',
      name: '',
      swiftBic: '',
    },
    transferInformation: {
      amount: undefined,
      label: '',
      supportingDocument: undefined,
    },
  });

  const context = useMemo<Context>(
    () => ({
      ...state,
      dispatch,
    }),
    [state, dispatch],
  );

  return (
    <BankTransferRequestsNewContext.Provider value={context}>
      {children}
    </BankTransferRequestsNewContext.Provider>
  );
};

export default BankTransferRequestsNewProvider;
